import { NormalizedNumber } from '@sparkdotfi/common-universal'

import { PositionOverview } from '../types'
import { NativeAssetInfo } from '@/config/chain/types'
import { DialogFormNormalizedData } from '@/features/dialogs/common/logic/form'
import { AaveData } from '@/features/sparklend/common/data/market-info/aave-data-layer/query'
import { MarketInfo } from '@/features/sparklend/common/data/market-info/marketInfo'
import { calculateVariableBorrowRate, rateToYield } from '@/features/sparklend/common/data/market-info/math'
import { updatePositionSummary } from '@/features/sparklend/common/data/market-info/updatePositionSummary'

export interface CalculateUpdatePositionOverviewParams {
  tokenToBorrow: DialogFormNormalizedData
  marketInfo: MarketInfo
  aaveData: AaveData
  nativeAssetInfo: NativeAssetInfo
  currentPositionOverview: PositionOverview
}

export function calculateUpdatePositionOverview({
  tokenToBorrow,
  marketInfo,
  aaveData,
  nativeAssetInfo,
  currentPositionOverview,
}: CalculateUpdatePositionOverviewParams): PositionOverview | undefined {
  const updatedUserSummary = updatePositionSummary({
    borrows: [tokenToBorrow],
    marketInfo,
    aaveData,
    nativeAssetInfo,
  })

  const cappedBorrowValue = NormalizedNumber.min(tokenToBorrow.value, tokenToBorrow.reserve.availableLiquidity) // if the borrowed value is greater than the available liquidity, borrow APY calculations don't make sense
  const currentBorrowApy = tokenToBorrow.reserve.variableBorrowApy
  const updatedBorrowApy = rateToYield(
    calculateVariableBorrowRate({
      ...tokenToBorrow.reserve,
      totalVariableDebt: tokenToBorrow.reserve.totalVariableDebt.plus(cappedBorrowValue),
      availableLiquidity: tokenToBorrow.reserve.availableLiquidity.minus(cappedBorrowValue),
    }),
  )
  const significantBorrowDifference = currentBorrowApy
    ? updatedBorrowApy.minus(currentBorrowApy).abs().gt(0.0001)
    : undefined

  const updatedPositionOverview: PositionOverview | undefined = tokenToBorrow.value.eq(0)
    ? undefined
    : {
        ...currentPositionOverview,
        debt: currentPositionOverview.debt.plus(tokenToBorrow.value),
        healthFactor: updatedUserSummary.healthFactor,
        borrowAPY: significantBorrowDifference ? updatedBorrowApy : currentBorrowApy, // pretend it's the same if the difference is less than 0.01%
      }

  return updatedPositionOverview
}
